/**
 * Node Helper Utilities
 * Helper functions for working with the universal node system
 */

import type { 
  NodeDefinition, 
  WorkflowNode, 
  WorkflowGraph, 
  NodeFieldDefinition,
  WorkflowConnection
} from '../types/node-definitions';

/**
 * Get the definition for a workflow node
 */
export function getNodeDefinition(
  node: WorkflowNode, 
  definitions: Record<string, NodeDefinition>
): NodeDefinition | null {
  return definitions[node.definitionId] || null;
}

/**
 * Get the current value of a field for a workflow node
 */
export function getFieldValue(
  node: WorkflowNode,
  fieldId: string,
  definition?: NodeDefinition
): any {
  // First check if value is set in node instance
  if (node.values.hasOwnProperty(fieldId)) {
    return node.values[fieldId];
  }
  
  // Fall back to default value from definition
  if (definition?.fields[fieldId]?.default !== undefined) {
    return definition.fields[fieldId].default;
  }
  
  return null;
}

/**
 * Set a field value for a workflow node
 */
export function setFieldValue(
  node: WorkflowNode,
  fieldId: string,
  value: any
): WorkflowNode {
  return {
    ...node,
    values: {
      ...node.values,
      [fieldId]: value
    }
  };
}

/**
 * Get all fields that should be shown in the properties panel
 */
export function getPropertiesFields(
  definition: NodeDefinition
): Array<{id: string; field: NodeFieldDefinition}> {
  return Object.entries(definition.fields)
    .filter(([_, field]) => field.showInProperties !== false)
    .map(([id, field]) => ({ id, field }));
}

/**
 * Get all fields that should be shown in the canvas
 */
export function getCanvasFields(
  definition: NodeDefinition
): Array<{id: string; field: NodeFieldDefinition}> {
  return Object.entries(definition.fields)
    .filter(([_, field]) => field.showInCanvas === true)
    .map(([id, field]) => ({ id, field }));
}

/**
 * Validate if a field value meets its constraints
 */
export function validateFieldValue(
  value: any,
  field: NodeFieldDefinition
): { valid: boolean; error?: string } {
  // Required check
  if (field.required && (value === null || value === undefined || value === '')) {
    return { valid: false, error: `${field.label} is required` };
  }
  
  // Type-specific validation
  switch (field.type) {
    case 'number':
    case 'range':
      if (typeof value === 'number') {
        if (field.min !== undefined && value < field.min) {
          return { valid: false, error: `${field.label} must be at least ${field.min}` };
        }
        if (field.max !== undefined && value > field.max) {
          return { valid: false, error: `${field.label} must be at most ${field.max}` };
        }
      }
      break;
      
    case 'string':
    case 'text':
      if (typeof value === 'string') {
        if (field.min !== undefined && value.length < field.min) {
          return { valid: false, error: `${field.label} must be at least ${field.min} characters` };
        }
        if (field.max !== undefined && value.length > field.max) {
          return { valid: false, error: `${field.label} must be at most ${field.max} characters` };
        }
      }
      break;
      
    case 'select':
      if (field.options && !field.options.some(opt => opt.value === value)) {
        return { valid: false, error: `${field.label} must be one of the available options` };
      }
      break;
  }
  
  return { valid: true };
}

/**
 * Validate all fields of a workflow node
 */
export function validateNode(
  node: WorkflowNode,
  definition: NodeDefinition
): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  
  for (const [fieldId, field] of Object.entries(definition.fields)) {
    const value = getFieldValue(node, fieldId, definition);
    const validation = validateFieldValue(value, field);
    
    if (!validation.valid && validation.error) {
      errors[fieldId] = validation.error;
    }
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Create a new workflow node instance from a definition
 */
export function createNodeInstance(
  definitionId: string,
  position: { x: number; y: number },
  initialValues: Record<string, any> = {}
): WorkflowNode {
  return {
    definitionId,
    values: {
      position,
      ...initialValues
    }
  };
}

/**
 * Generate a unique node ID
 */
export function generateNodeId(existingIds: string[] = []): string {
  let counter = 1;
  let id = `node_${counter}`;
  
  while (existingIds.includes(id)) {
    counter++;
    id = `node_${counter}`;
  }
  
  return id;
}

/**
 * Generate a unique connection ID
 */
export function generateConnectionId(existingIds: string[] = []): string {
  let counter = 1;
  let id = `conn_${counter}`;
  
  while (existingIds.includes(id)) {
    counter++;
    id = `conn_${counter}`;
  }
  
  return id;
}

/**
 * Check if a field should be visible based on conditional logic
 */
export function isFieldVisible(
  field: NodeFieldDefinition,
  node: WorkflowNode,
  definition: NodeDefinition
): boolean {
  if (!field.conditional) {
    return true;
  }
  
  const dependentFieldValue = getFieldValue(node, field.conditional.field, definition);
  return dependentFieldValue === field.conditional.value;
}

/**
 * Get all connections for a specific node
 */
export function getNodeConnections(
  nodeId: string,
  connections: WorkflowConnection[]
): {
  incoming: WorkflowConnection[];
  outgoing: WorkflowConnection[];
} {
  const incoming = connections.filter(conn => conn.to.nodeId === nodeId);
  const outgoing = connections.filter(conn => conn.from.nodeId === nodeId);
  
  return { incoming, outgoing };
}

/**
 * Check if two nodes can be connected (basic validation)
 */
export function canConnect(
  fromNodeId: string,
  fromOutputId: string,
  toNodeId: string,
  toInputId: string,
  workflow: WorkflowGraph,
  definitions: Record<string, NodeDefinition>
): { canConnect: boolean; reason?: string } {
  // Can't connect to self
  if (fromNodeId === toNodeId) {
    return { canConnect: false, reason: 'Cannot connect node to itself' };
  }
  
  const fromNode = workflow.nodes[fromNodeId];
  const toNode = workflow.nodes[toNodeId];
  
  if (!fromNode || !toNode) {
    return { canConnect: false, reason: 'Invalid node reference' };
  }
  
  const fromDef = definitions[fromNode.definitionId];
  const toDef = definitions[toNode.definitionId];
  
  if (!fromDef || !toDef) {
    return { canConnect: false, reason: 'Node definition not found' };
  }
  
  const fromOutput = fromDef.outputs.find(out => out.id === fromOutputId);
  const toInput = toDef.inputs.find(inp => inp.id === toInputId);
  
  if (!fromOutput || !toInput) {
    return { canConnect: false, reason: 'Invalid socket reference' };
  }
  
  // Check if connection already exists
  const existingConnection = workflow.connections.find(conn => 
    conn.from.nodeId === fromNodeId &&
    conn.from.outputId === fromOutputId &&
    conn.to.nodeId === toNodeId &&
    conn.to.inputId === toInputId
  );
  
  if (existingConnection) {
    return { canConnect: false, reason: 'Connection already exists' };
  }
  
  // Check if input already has connection (and doesn't allow multiple)
  if (!toInput.multiple) {
    const hasConnection = workflow.connections.some(conn =>
      conn.to.nodeId === toNodeId && conn.to.inputId === toInputId
    );
    
    if (hasConnection) {
      return { canConnect: false, reason: 'Input already connected' };
    }
  }
  
  // Type compatibility check (basic)
  if (fromOutput.type !== toInput.type && toInput.type !== 'any') {
    return { canConnect: false, reason: `Type mismatch: ${fromOutput.type} -> ${toInput.type}` };
  }
  
  return { canConnect: true };
}

/**
 * Clone a workflow node (deep copy)
 */
export function cloneNode(node: WorkflowNode): WorkflowNode {
  return {
    ...node,
    values: { ...node.values },
    _runtime: node._runtime ? { ...node._runtime } : undefined
  };
}

/**
 * Export workflow to JSON string
 */
export function exportWorkflowToJSON(workflow: WorkflowGraph): string {
  // Remove runtime data before export
  const cleanWorkflow: WorkflowGraph = {
    ...workflow,
    nodes: Object.fromEntries(
      Object.entries(workflow.nodes).map(([id, node]) => [
        id,
        {
          definitionId: node.definitionId,
          values: node.values
          // Exclude _runtime
        }
      ])
    )
  };
  
  return JSON.stringify(cleanWorkflow, null, 2);
}

/**
 * Import workflow from JSON string
 */
export function importWorkflowFromJSON(json: string): WorkflowGraph {
  try {
    const workflow = JSON.parse(json) as WorkflowGraph;
    
    // Validate basic structure
    if (!workflow.meta || !workflow.nodes || !workflow.connections) {
      throw new Error('Invalid workflow format');
    }
    
    return workflow;
  } catch (error) {
    throw new Error(`Failed to import workflow: ${error instanceof Error ? error.message : String(error)}`);
  }
}